// Player status and news on a simulated league (PLAYER_NEWS.md): one line
// per fact, written by us. The sim derives everything on read: injury
// statuses become practice reports and designations on Wednesday, Friday,
// and Sunday morning; a few depth-chart lines a week for owned players;
// league events from the transaction log. Nothing is scraped.

import type { NewsFilter, NewsView } from '@fsp/domain';
import { football } from '@fsp/sports-football';
import type { SimPlayer, SimState } from './league';
import { hashSeed, rng } from './rng';
import { playerSummary, poolStanding, teamRef } from './views';

const H = 3600 * 1000;
const statuses = new Map(football.injuryStatuses.map((s) => [s.id, s]));

interface Item {
  playerId: string;
  kind: NewsView['kind'];
  severity: NewsView['severity'];
  headline: string;
  body: string | null;
  source: string;
  link: string | null;
  at: number;
}

function iso(ms: number): string {
  return new Date(ms).toISOString();
}

/** The practice week behind a designation: reports on Wednesday and Friday, the call on Sunday morning. */
function statusItems(state: SimState, p: SimPlayer): Item[] {
  if (!p.injury) return [];
  const sun = state.sundayUtcMidnight;
  const wed = sun - 4 * 24 * H + 18 * H;
  const fri = sun - 2 * 24 * H + 20 * H;
  const sunMorning = sun + 15.5 * H;
  const note = p.injury.note.replace(/\.$/, '');
  const base = { playerId: p.id, body: null, source: 'Sim Wire', link: null };
  if (p.injury.status === 'O')
    return [
      {
        ...base,
        kind: 'practice',
        severity: 'warn',
        headline: 'Did not practice Wednesday.',
        at: wed,
      },
      {
        ...base,
        kind: 'practice',
        severity: 'warn',
        headline: 'Did not practice Friday.',
        at: fri,
      },
      { ...base, kind: 'status', severity: 'out', headline: `Ruled out. ${note}.`, at: sunMorning },
    ];
  const def = statuses.get(p.injury.status);
  const name = def?.name ?? p.injury.status;
  const severity: NewsView['severity'] =
    def?.severity === 'out' || def?.severity === 'likely-out' ? 'out' : 'warn';
  return [
    {
      ...base,
      kind: 'practice',
      severity: 'warn',
      headline: 'Limited in practice Wednesday.',
      at: wed,
    },
    { ...base, kind: 'status', severity, headline: `${name}. ${note}.`, at: fri },
  ];
}

const DEPTH_LINES = [
  'Named the starter at {pos}.',
  'Expected to see more work this week.',
  'Drawing praise in practice. Volume should follow.',
  'Back with the first team after a quiet week.',
  'Coaches want him more involved. So does his fantasy manager.',
];

/** A few depth-chart lines a week, for owned, healthy players, drawn from the seed. */
function depthItems(state: SimState): Item[] {
  const owned = state.teams.flatMap((t) => t.roster);
  const r = rng(hashSeed(state.seed, 'news', state.currentPeriod));
  const pool = owned.map((id) => state.players.get(id)!).filter((p) => !p.injury);
  const picks = r.shuffle(pool).slice(0, 5);
  const thu = state.sundayUtcMidnight - 3 * 24 * H + 14 * H;
  return picks.map((p, i) => {
    const line = DEPTH_LINES[r.int(0, DEPTH_LINES.length - 1)]!;
    const pos = football.positions.find((x) => x.id === p.positions[0])?.name ?? p.positions[0];
    return {
      playerId: p.id,
      kind: 'move',
      severity: 'info',
      headline: line.replace('{pos}', pos),
      body: null,
      source: 'Sim Wire',
      link: null,
      at: thu + i * 7 * 60 * 1000,
    };
  });
}

/** Added, claimed, dropped, traded in this league, from the transaction log. */
function leagueItems(state: SimState): Item[] {
  const out: Item[] = [];
  const owner = (teamId: string) =>
    state.teams.find((t) => t.id === teamId)?.ownerName ?? 'Someone';
  const teamName = (teamId: string) => state.teams.find((t) => t.id === teamId)?.name ?? 'a team';
  for (const tx of state.waiverState.transactions) {
    if (tx.reversedBy) continue;
    const base = { body: null, source: 'League', link: null, at: tx.at, severity: 'info' as const };
    for (const id of tx.added)
      out.push({
        ...base,
        playerId: id,
        kind: 'league',
        headline:
          tx.kind === 'trade'
            ? `Traded to ${teamName(tx.teamId)}.`
            : tx.kind === 'claim'
              ? `Claimed by ${owner(tx.teamId)}${tx.faab !== null ? ` for $${tx.faab}` : ''}.`
              : `Added by ${owner(tx.teamId)}.`,
      });
    if (tx.kind !== 'trade')
      for (const id of tx.dropped)
        out.push({
          ...base,
          playerId: id,
          kind: 'league',
          headline: `Dropped by ${owner(tx.teamId)}.`,
        });
  }
  return out;
}

function toView(
  state: SimState,
  item: Item,
  now: number,
  mine: ReadonlySet<string>,
  standing: ReturnType<typeof poolStanding>,
): NewsView {
  const p = state.players.get(item.playerId)!;
  const team = state.teams.find((t) => t.roster.includes(item.playerId)) ?? null;
  const held = standing(item.playerId);
  return {
    id: `${item.playerId}:${item.kind}:${item.at}`,
    player: playerSummary(state, p),
    headline: item.headline,
    source: item.source,
    publishedAt: iso(item.at),
    kind: item.kind,
    severity: item.severity,
    body: item.body,
    link: item.link,
    mine: mine.has(item.playerId),
    team: team ? teamRef(state, team, now) : null,
    inLeague: held?.availability ?? null,
    action: held?.action ?? null,
    reason: held?.reason ?? null,
  };
}

function everything(state: SimState): Item[] {
  if (state.status !== 'in-season') return [];
  const items: Item[] = [];
  for (const p of state.players.values()) items.push(...statusItems(state, p));
  items.push(...depthItems(state));
  items.push(...leagueItems(state));
  return items;
}

/** The feed, newest first, up to `now`, filtered (PLAYER_NEWS.md). */
export function newsItems(
  state: SimState,
  now: number,
  filter: NewsFilter = 'mine',
  limit = 100,
): NewsView[] {
  const me = state.teams.find((t) => t.isMine);
  const mine = new Set(me?.roster ?? []);
  const standing = poolStanding(state, now);
  return everything(state)
    .filter((i) => i.at <= now)
    .filter((i) => {
      switch (filter) {
        case 'mine':
          return mine.has(i.playerId);
        case 'injuries':
          return i.kind === 'status' || i.kind === 'practice';
        case 'moves':
          return i.kind === 'league' || i.kind === 'move';
        default:
          return true;
      }
    })
    .sort((a, b) => b.at - a.at)
    .slice(0, limit)
    .map((i) => toView(state, i, now, mine, standing));
}

/** One player's news, newest first. */
export function playerNews(state: SimState, playerId: string, now: number): NewsView[] {
  const me = state.teams.find((t) => t.isMine);
  const mine = new Set(me?.roster ?? []);
  const standing = poolStanding(state, now);
  return everything(state)
    .filter((i) => i.playerId === playerId && i.at <= now)
    .sort((a, b) => b.at - a.at)
    .map((i) => toView(state, i, now, mine, standing));
}
